import React, { useMemo, useEffect, useCallback } from "react";
import ChatBotOption from "./ChatBotOption";
import PhoneNumberField from "./PhoneNumberField";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";

import {
  closeSignupModal,
  openSignupModal,
  openThanksPopup,
  openRegisterSuccessPrompt,
} from "../../features/slices/ModalSlice";
import { updateSelectedItem } from "../../features/slices/CommonSlice";
import {
  registerAndSendOtp,
  resetError,
  storePhoneAndCountry,
} from "../../features/slices/UserSlice";
import {
  selectRegistrationConfig,
  selectRegistrationDefaults,
  selectRegistrationFields,
} from "../../features/slices/AppConfigSlice";
import {
  getMaxDateOfBirth,
  getOrderedRegistrationFields,
  getPasswordRuleStatus,
  isAtLeastMinAge,
  getRegistrationFieldLabel,
  MIN_REGISTRATION_AGE,
  validateRegistrationField,
} from "../../utils/registrationConfig";
import {
  isPhoneNumberAcceptable,
  parsePhoneInput,
  restrictPhoneInput,
  getIndiaNationalDigitCount,
} from "../../utils/phoneValidation";
import { openInNewTab } from "../../utils/openInNewTab";

const INITIAL_FORM_VALUES = {
  username: "",
  email: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  country: "",
  state: "",
  city: "",
  password: "",
  referral_code: "",
};

// Input types a config-driven field may ask for; anything else falls back to text
const SUPPORTED_INPUT_TYPES = {
  text: "text",
  email: "email",
  number: "number",
  tel: "tel",
  url: "url",
  date: "date",
};

function FieldLabel({ label, required }) {
  return (
    <b>
      {label}
      {required ? (
        <span aria-hidden="true" className="required-asterisk">
          {" *"}
        </span>
      ) : null}
    </b>
  );
}

export default function SignupForm() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const registrationConfig = useSelector(selectRegistrationConfig);
  const registrationFields = useSelector(selectRegistrationFields);
  const registrationDefaults = useSelector(selectRegistrationDefaults);
  const registrationLoading = useSelector(
    (state) => state.appConfig.registrationLoading
  );
  const registrationError = useSelector(
    (state) => state.appConfig.registrationError
  );

  const orderedFields = useMemo(
    () => getOrderedRegistrationFields(registrationFields),
    [registrationFields]
  );

  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);
  const [phone, setPhone] = useState("");
  const [phoneValue, setPhoneValue] = useState();
  const [phoneInputEpoch, setPhoneInputEpoch] = useState(0);
  const [phoneE164, setPhoneE164] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [toc, setTOC] = useState(false);
  const [receiveOffers, setReceiveOffers] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState("IN");
  const [localError, setLocalError] = useState("");
  const [referralFromUrl, setReferralFromUrl] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const showField = useCallback(
    (key) => Boolean(registrationFields[key]),
    [registrationFields]
  );

  // Custom fields have no slot in INITIAL_FORM_VALUES; seed them so their
  // inputs are controlled from the first render.
  useEffect(() => {
    setFormValues((prev) => {
      const missing = orderedFields.filter(({ key }) => prev[key] === undefined);
      if (!missing.length) return prev;

      const seeded = { ...prev };
      missing.forEach(({ key }) => {
        seeded[key] = "";
      });
      return seeded;
    });
  }, [orderedFields]);

  useEffect(() => {
    const defaultCountryCode = registrationDefaults?.country;
    if (!defaultCountryCode) return;

    setDefaultCountry(defaultCountryCode);
    setFormValues((prev) => ({
      ...prev,
      country: prev.country || defaultCountryCode,
    }));
  }, [registrationDefaults?.country]);

  useEffect(() => {
    const path = window.location.pathname;
    const referralCode = path.split("/refer/")[1];
    if (referralCode) {
      setFormValues((prev) => ({ ...prev, referral_code: referralCode }));
      setReferralFromUrl(true);
      if (isMobile) {
        dispatch(updateSelectedItem("signup"));
      } else {
        dispatch(openSignupModal());
      }
    }
  }, [dispatch, isMobile]);

  const updateField = (key, value) => {
    setLocalError("");
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneNumberChange = (value) => {
    setLocalError("");
    const restricted = restrictPhoneInput(value, defaultCountry);
    // Truncating to the same E.164 can skip React updates; remount so UI stays at 10 digits
    if (value && restricted !== value) {
      setPhoneInputEpoch((epoch) => epoch + 1);
    }
    setPhoneValue(restricted || undefined);
    const parsed = parsePhoneInput(restricted);
    setPhoneE164(parsed.e164);
    setIsPhoneValid(parsed.isValid);
    if (parsed.isValid) {
      if (parsed.country) setDefaultCountry(parsed.country);
      setCountryCode(parsed.countryCode);
      setPhone(parsed.nationalNumber);
    } else {
      setCountryCode("");
      setPhone("");
    }
  };

  const handleIndiaPhoneKeyDown = (event) => {
    // Gated on the value, not on `defaultCountry`: the count is 0 for any
    // number that isn't +91, and the country state lags a country change.
    const isDigit = event.key >= "0" && event.key <= "9";
    if (!isDigit) return;
    if (getIndiaNationalDigitCount(phoneValue) >= 10) {
      event.preventDefault();
    }
  };

  const passwordRules = useMemo(
    () => getPasswordRuleStatus(formValues.password),
    [formValues.password]
  );

  // drives only how the button looks. it stays clickable while it looks
  // inactive so pressing it still reports what is missing instead of doing
  // nothing
  const requiredFilled = useMemo(() => {
    if (!toc || !receiveOffers) return false;
    const hasValue = (value) => String(value ?? "").trim() !== "";

    return orderedFields.every(({ key, required }) => {
      if (!required) return true;
      if (key === "phone") return Boolean(phoneE164);
      if (key === "password") {
        return hasValue(formValues.password) && hasValue(confirmPassword);
      }
      return hasValue(formValues[key]);
    });
  }, [orderedFields, formValues, phoneE164, confirmPassword, toc, receiveOffers]);

  const validateForm = () => {
    for (const { key, ...fieldConfig } of orderedFields) {
      if (key === "password") {
        const passwordError = validateRegistrationField(
          key,
          fieldConfig,
          formValues.password
        );
        if (passwordError) return passwordError;
        if (formValues.password !== confirmPassword) {
          return "Both passwords should match";
        }
        continue;
      }

      if (key === "phone") {
        if (fieldConfig.required && !phoneE164) {
          return "Phone number is not valid";
        }
        if (fieldConfig.required || phoneE164) {
          if (!isPhoneNumberAcceptable(phoneE164) || !isPhoneValid) {
            return "Phone number is not valid";
          }
        }
        continue;
      }

      const error = validateRegistrationField(
        key,
        fieldConfig,
        formValues[key]
      );
      if (error) return error;
    }

    return null;
  };

  const signUpUser = (event) => {
    event.preventDefault();
    const form = document.querySelector(".needs-validation");
    if (!form?.checkValidity()) return;

    const validationError = validateForm();
    if (validationError) {
      // Client validation only — clear any leftover API error so one message shows
      dispatch(resetError());
      setLocalError(validationError);
      return;
    }

    setLocalError("");
    const payload = {
      toc,
      receiveOffers,
      password: formValues.password,
      confirmPassword: formValues.password,
    };

    // Driven off the config so custom fields are submitted with the known ones
    orderedFields.forEach(({ key }) => {
      if (key === "password") return;

      if (key === "phone") {
        payload.phone = phone;
        payload.countryCode = countryCode;
        return;
      }

      const value = formValues[key];
      payload[key] = typeof value === "string" ? value.trim() : value ?? "";
    });

    if (showField("country") && !payload.country) {
      payload.country = registrationDefaults?.country;
    }
    if (registrationDefaults?.currency) {
      payload.currency = registrationDefaults.currency;
    }

    if (showField("phone")) {
      dispatch(storePhoneAndCountry({ countryCode, phone }));
    }
    dispatch(resetError());
    dispatch(registerAndSendOtp(payload))
      .unwrap()
      .then((result) => {
        dispatch(closeSignupModal());
        if (isMobile) {
          dispatch(updateSelectedItem(null));
        }

        if (result.skipOtp) {
          dispatch(openThanksPopup());
          return;
        }

        dispatch(openRegisterSuccessPrompt());
      })
      .catch(() => {
        // API errors land on user.error via registerAndSendOtp.rejected
      });
  };

  const styles = {
    backgroundColor: isMobile ? "#111111" : "none",
    height: "auto",
    paddingBottom: isMobile ? "100px" : "auto",
  };

  useEffect(() => {
    if (!user.error && !localError) return;
    // the popup needs no scrolling: its notice stays pinned above the form
    if (isMobile) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [user.error, localError, isMobile]);

  const renderField = (field) => {
    const { key, required, min_length, max_length } = field;
    const label = getRegistrationFieldLabel(key, field);
    const minLen = min_length;
    const maxLen = max_length;

    if (key === "phone") {
      return (
        <div key={key}>
          <FieldLabel label={label} required={required} />
          <PhoneNumberField
            key={`phone-${defaultCountry}-${phoneInputEpoch}`}
            placeholder="Enter phone number"
            className={`mb-3 text-input ${isPhoneValid ? "is-valid form-control" : ""
              }`}
            value={phoneValue}
            onChange={handlePhoneNumberChange}
            onCountryChange={(country) => {
              if (country) setDefaultCountry(country);
            }}
            defaultCountry={defaultCountry}
            numberInputProps={{
              onKeyDown: handleIndiaPhoneKeyDown,
              inputMode: "numeric",
            }}
            autoComplete="off"
            required={required}
          />
        </div>
      );
    }

    if (key === "password") {
      return (
        <React.Fragment key={key}>
          <FieldLabel label={label} required={required} />
          <div className="password overflow-hidden text-input">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              required={required}
              minLength={Math.max(8, minLen ?? 0)}
              maxLength={maxLen}
              className="form-control"
              value={formValues.password}
              onChange={(e) => updateField("password", e.target.value)}
              placeholder="Enter Password"
              autoComplete="off"
            />
            <i
              className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}
              onClick={() => setShowPassword((prev) => !prev)}
              style={{ fontSize: "18px", marginTop: "-3px" }}
              role="button"
              tabIndex={0}
              aria-label={showPassword ? "Hide password" : "Show password"}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowPassword((prev) => !prev);
                }
              }}
            ></i>
          </div>
          {formValues.password.length > 0 &&
            passwordRules.some((rule) => !rule.met) && (
              <ul className="password-requirements mb-3">
                {passwordRules.map((rule) => (
                  <li
                    key={rule.key}
                    className={
                      rule.met
                        ? "password-requirement met"
                        : "password-requirement"
                    }
                  >
                    {rule.label}
                  </li>
                ))}
              </ul>
            )}
          <FieldLabel label="Confirm Password" required={required} />
          <div className="password overflow-hidden text-input">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              className="text-input form-control"
              required={required}
              value={confirmPassword}
              onChange={(e) => {
                setLocalError("");
                setConfirmPassword(e.target.value);
              }}
              placeholder="Confirm Password"
            />
            <i
              className={`bi ${showConfirmPassword ? "bi-eye" : "bi-eye-slash"
                }`}
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              style={{ fontSize: "18px", marginTop: "-3px" }}
              role="button"
              tabIndex={0}
              aria-label={
                showConfirmPassword
                  ? "Hide confirm password"
                  : "Show confirm password"
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowConfirmPassword((prev) => !prev);
                }
              }}
            ></i>
          </div>
          {confirmPassword.length > 0 &&
            formValues.password !== confirmPassword && (
              <p className="normal--message">Both passwords should match</p>
            )}
        </React.Fragment>
      );
    }

    if (key === "referral_code") {
      return (
        <div key={key}>
          <FieldLabel label={label} required={required} />
          <div className="referral-code overflow-hidden d-flex justify-content-between text-input">
            <input
              type="text"
              name="referral_code"
              value={formValues.referral_code}
              readOnly={referralFromUrl}
              required={required}
              minLength={minLen}
              maxLength={maxLen}
              className="text-input form-control"
              onChange={(e) => updateField("referral_code", e.target.value)}
              id="rf_code"
            />
          </div>
        </div>
      );
    }

    if (key === "date_of_birth") {
      const dobTooYoung =
        formValues.date_of_birth &&
        !isAtLeastMinAge(formValues.date_of_birth);

      return (
        <div key={key}>
          <FieldLabel label={label} required={required} />
          <input
            type="date"
            name={key}
            required={required}
            max={getMaxDateOfBirth()}
            className="form-control text-input mb-3"
            value={formValues.date_of_birth}
            onChange={(e) => updateField(key, e.target.value)}
          />
          {dobTooYoung && (
            <p className="normal--message">
              You must be at least {MIN_REGISTRATION_AGE} years old to register
            </p>
          )}
        </div>
      );
    }

    const value = formValues[key] ?? "";
    const options = Array.isArray(field.options)
      ? field.options
      : Array.isArray(field.choices)
        ? field.choices
        : null;

    if (options) {
      return (
        <div key={key}>
          <FieldLabel label={label} required={required} />
          <select
            name={key}
            required={required}
            className="form-control text-input mb-3"
            value={value}
            onChange={(e) => updateField(key, e.target.value)}
          >
            <option value="">{`Select ${label}`}</option>
            {options.map((option) => {
              const isObject = option && typeof option === "object";
              const optionValue = isObject ? option.value ?? option.id : option;
              const optionLabel = isObject
                ? option.label ?? option.name ?? optionValue
                : option;

              return (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    const inputType =
      SUPPORTED_INPUT_TYPES[field.type] || (key === "email" ? "email" : "text");

    return (
      <div key={key}>
        <FieldLabel label={label} required={required} />
        <input
          type={inputType}
          name={key}
          required={required}
          minLength={minLen}
          maxLength={maxLen}
          className="form-control text-input mb-3"
          value={value}
          onChange={(e) => updateField(key, e.target.value)}
          placeholder={`Enter ${label}`}
          autoComplete="off"
        />
      </div>
    );
  };

  if (registrationLoading && !registrationConfig) {
    return (
      <div className="signup-form-inputs px-3 pt-3 text-center">
        <p className="text-muted">Loading registration form…</p>
      </div>
    );
  }

  if (registrationError && !registrationConfig) {
    return (
      <div className="signup-form-inputs px-3 pt-3 text-center">
        <p className="message">{registrationError}</p>
        <ChatBotOption />
      </div>
    );
  }

  return (
    <div
      className={`${isMobile ? "form-mobile signup-form-inputs px-3 pt-3" : ""
        }`}
      style={styles}
    >
      {isMobile && (
        <p
          className="text-center"
          style={{ color: "#EFC04B" }}
          id="signupValidation"
        >
          Signup
        </p>
      )}

      <div className="signup-notice">
        <div className="incorrect-details-div mb-3">
          {(localError || user.error) && (
            <p className="text-center message">{localError || user.error}</p>
          )}
          <ChatBotOption />
        </div>
      </div>

      <form className="needs-validation signup-form" onSubmit={signUpUser}>
        {orderedFields.map(renderField)}

        <div className="checkboxes pt-4">
          <span className="d-flex">
            <input
              className="form-check-input mt-0"
              type="checkbox"
              onChange={(e) => setTOC(e.target.checked)}
              required
              aria-label="Terms and conditions"
            />
            <p className="remember-me">
              I confirm I’m 18+ and agree with the terms and conditions. For more
              information on how we process your personal data please see our{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => openInNewTab("/privacy-policy", e)}
                style={{ textDecoration: "none" }}
              >
                privacy policy.
              </a>
            </p>
          </span>

          <span className="d-flex">
            <input
              className="form-check-input mt-0"
              type="checkbox"
              onChange={(e) => setReceiveOffers(e.target.checked)}
              aria-label="Marketing offers"
              required
            />
            <p className="remember-me">
              I agree to receive free spins, exclusive offers and awesome updates
              via my registered contact details. Opt out is available at any time
              on your profile page.
            </p>
          </span>
        </div>

        <button
          className="login-btn mb-3 mt-3 p-2"
          type="submit"
          aria-disabled={!requiredFilled}
        >
          Signup
        </button>
      </form>
    </div>
  );
}
